/**
 * listening-exam-data — 英語聽力卷定稿內容。
 *
 * 單一真相:docs/spec/LISTENING_EXAM_SPEC.md §2(逐字照 spec 表格,別自己改題 /
 * 改答案)。內容正確性已我方定稿,仍須教育專家複審(K-3 英語老師人設,見 memory
 * feedback_content_needs_education_expert_review)。
 *
 * 範圍 = 翰林英語段考聽力卷 Review 2:jobs 職業 + 家庭成員 + Wh- 問句 +
 * Is he/she…? Yes/No + 母音/子音辨識。
 *
 * 設計要點:
 *   - `audio.spoken` 永遠是「完整正確」字 / 句(TTS 只唸對的;干擾項只印不唸)。
 *   - `audio.id` = 預產 mp3 檔名 public/audio/listening/henlin-review-2/<id>.mp3。
 *   - 總分 12 + 12 + 10 + 27 = 61 分(MVP)。
 *
 * 學習/遊戲分離:純學習側資料,不觸碰任何戰鬥數值。
 */

import type { ListeningExam } from './listening-exam-types';

/** 翰林 Review 2 英語聽力卷(定稿)。 */
export const HENLIN_REVIEW_2: ListeningExam = {
  id: 'henlin-review-2',
  title: '英語聽力卷 · Review 2',
  textbook: '翰林',
  parts: [
    // ── Part A — Listen and Check(每題 2 分,共 12 分;唸正解,勾出相同發音字)──
    {
      kind: 'listen-check',
      label: 'Part A',
      title: 'Listen and Check',
      instructions: '仔細聽,把和聽力相同的發音字打 ✓。每題 2 分,共 12 分。',
      pointsEach: 2,
      // minimal pair 皆取自策展閉集池(spec §7.2),確保 golden ⊆ 池(§7.4-4)。
      // 原卷 #2 fan/fun、#6 map/mop 非池內對(fan∈[man,fan]、fun∈[sun,fun];mop 不在池)→
      // 改用池內等義對 [sun,fun](答 fun)、[map,mat](答 map),維持相同 phonics 對立。
      items: [
        { id: 'a1', options: ['bug', 'bun'], audio: { id: 'a1', spoken: 'bug' }, answerIndex: 0 },
        { id: 'a2', options: ['sun', 'fun'], audio: { id: 'a2', spoken: 'fun' }, answerIndex: 1 },
        { id: 'a3', options: ['run', 'rug'], audio: { id: 'a3', spoken: 'run' }, answerIndex: 0 },
        { id: 'a4', options: ['box', 'fox'], audio: { id: 'a4', spoken: 'fox' }, answerIndex: 1 },
        { id: 'a5', options: ['hot', 'hop'], audio: { id: 'a5', spoken: 'hop' }, answerIndex: 1 },
        { id: 'a6', options: ['map', 'mat'], audio: { id: 'a6', spoken: 'map' }, answerIndex: 0 },
      ],
    },
    // ── Part B — Listen and Choose(每題 3 分,共 12 分;唸問句,選文法相符回應)──
    {
      kind: 'listen-choose',
      label: 'Part B',
      title: 'Listen and Choose',
      instructions: '仔細聽,選出與聽力相符的答。每題 3 分,共 12 分。',
      pointsEach: 3,
      items: [
        {
          id: 'b1',
          audio: { id: 'b1', spoken: 'Is he a nurse?' },
          choices: ["No, she's not.", "No, he's not.", "No, I'm not."],
          answerIndex: 1,
        },
        {
          id: 'b2',
          audio: { id: 'b2', spoken: 'Are you a student?' },
          choices: ['Yes, he is.', 'Yes, she is.', 'Yes, I am.'],
          answerIndex: 2,
        },
        {
          id: 'b3',
          audio: { id: 'b3', spoken: 'Is she a doctor?' },
          choices: ['Yes, he is.', 'Yes, she is.', 'Yes, I am.'],
          answerIndex: 1,
        },
        {
          id: 'b4',
          audio: { id: 'b4', spoken: "Who's he?" },
          choices: ["He's my brother.", "She's my sister.", "She's my mom."],
          answerIndex: 0,
        },
      ],
    },
    // ── Part C — Listen and Mark ○ or ×(每題 2 分,共 10 分;相同 ○、不同 ×)──
    {
      kind: 'listen-mark',
      label: 'Part C',
      title: 'Listen and Mark',
      instructions: '聽到的句子與題目相同打 ○、不同打 ×。每題 2 分,共 10 分。',
      pointsEach: 2,
      items: [
        { id: 'c1', printed: 'Is your dad a cook?', audio: { id: 'c1', spoken: 'Is your dad a cook?' }, answer: 'O' },
        { id: 'c2', printed: 'My sister is a nurse.', audio: { id: 'c2', spoken: 'My brother is a nurse.' }, answer: 'X' },
        { id: 'c3', printed: "He's a teacher.", audio: { id: 'c3', spoken: "He's a doctor." }, answer: 'X' },
        { id: 'c4', printed: "Who's she?", audio: { id: 'c4', spoken: "Who's he?" }, answer: 'X' },
        { id: 'c5', printed: "She's my grandma.", audio: { id: 'c5', spoken: "She's my grandma." }, answer: 'O' },
      ],
    },
    // ── Part D — Listen and Circle(每答 3 分,共 27 分 = 9 答;圈與聽到相同的字)──
    {
      kind: 'listen-circle',
      label: 'Part D',
      title: 'Listen and Circle',
      instructions: '把和聽力相同的發音字圈起來。每答 3 分,共 27 分。',
      pointsEach: 3,
      // Part D = 9 個獨立計分空格(每個一段 audio、一個圈選);每格 spoken 字面含答案。
      items: [
        {
          id: 'd1',
          before: 'Nice to',
          options: ['met', 'meet'],
          after: 'you.',
          audio: { id: 'd1', spoken: 'Nice to meet you.' },
          answerIndex: 1,
        },
        {
          id: 'd2',
          before: 'A:',
          options: ["What's", "Who's"],
          after: 'she?',
          audio: { id: 'd2', spoken: "Who's she?" },
          answerIndex: 1,
        },
        {
          id: 'd3',
          before: "B: She's my",
          options: ['sister', 'mother'],
          after: '.',
          audio: { id: 'd3', spoken: "She's my mother." },
          answerIndex: 1,
        },
        {
          id: 'd4',
          before: 'A:',
          options: ['Are', 'Is'],
          after: 'he a teacher?',
          audio: { id: 'd4', spoken: 'Is he a teacher?' },
          answerIndex: 1,
        },
        {
          id: 'd5',
          before: 'B: No,',
          options: ["he's", "she's"],
          after: 'not.',
          audio: { id: 'd5', spoken: "No, he's not." },
          answerIndex: 0,
        },
        {
          id: 'd6',
          before: 'I',
          options: ['is', 'am'],
          after: 'a cook.',
          audio: { id: 'd6', spoken: 'I am a cook.' },
          answerIndex: 1,
        },
        {
          id: 'd7',
          before: '',
          options: ['He', 'She'],
          after: 'is my dad.',
          audio: { id: 'd7', spoken: 'He is my dad.' },
          answerIndex: 0,
        },
        {
          id: 'd8',
          before: 'A: Is Amy a',
          options: ['student', 'doctor'],
          after: '?',
          audio: { id: 'd8', spoken: 'Is Amy a doctor?' },
          answerIndex: 1,
        },
        {
          id: 'd9',
          before: 'B:',
          options: ['Yes', 'No'],
          after: ', she is.',
          audio: { id: 'd9', spoken: 'Yes, she is.' },
          answerIndex: 0,
        },
      ],
    },
  ],
};

/** 全部聽力卷(目前只有 Review 2;日後可加)。 */
export const LISTENING_EXAMS: ListeningExam[] = [HENLIN_REVIEW_2];
